import axios from '@/api/provider';
import { downloadBlob } from '@/utils/download';

/**
 * Get metadata for all companies or a specified list
 * @param {string[]} companyNames - Optional list of company names to filter
 * @returns {Promise} Promise with company metadata
 */
const getCompanyMetadata = async (companyNames = null) => {
  const payload = companyNames
    ? { company_names: companyNames.map(name => name.trim()) }
    : {};

  return axios
    .post('company/metadata', payload)
    .then((response) => ({
      success: response.success,
      message: response.message,
      companies: (response.companies || []).map(company => ({
            id: company.id ?? null,
            companyName: company.company_name,
            companyIdentifier: company.company_identifier,
            companyRegion: company.company_region,
            firstOnboardedAt: company.first_onboarded_at,
            lastUpdated: company.last_updated ? {
              exposures: company.last_updated.exposures,
              alerts: company.last_updated.alerts,
              impacts: company.last_updated.impacts,
              eoi: company.last_updated.eoi,
              suppliers: company.last_updated.suppliers,
              reports: company.last_updated.reports
            } : null,
            documentCounts: company.document_counts ? {
              filingsProcessed: company.document_counts.filings_processed,
              transcriptsProcessed: company.document_counts.transcripts_processed,
              totalDocuments: company.document_counts.total_documents
            } : null,
            totalAlerts: company.total_alerts || 0,
            totalSchedules: company.total_schedules || 0,
            exposureTypes: company.exposure_types || [],
        status: company.status,
        logoUrl: company.logo_url || null,
        // ⚠ THE LOGO KEY, and the reason every list fell back to a name
        // lookup: `CompanyMetadata` has carried `domain` since it was added,
        // but this mapper dropped it, so no caller could see it.
        domain: company.domain || null
      }))
    }))
    .catch((error) => {
      console.error('Get company metadata error:', error);
      throw error;
    });
};

/**
 * Get company summary for the portfolio list page
 * Returns company name, identifier, priority, and counts.
 * Filtered by the user's active portfolio via auth token.
 * @returns {Promise} Promise with company summary list
 */
const getCompanySummary = async () => {
  return axios
    .get('company/summary')
    .then((response) => ({
      success: response.success,
      companies: (response.companies || []).map(company => ({
        companyName: company.company_name,
        companyIdentifier: company.company_identifier,
        priority: company.priority,
        priorityTitle: company.priority_event_title,
        priorityAction: company.priority_action,
        alertsCount: company.alerts_count || 0,
        eventsCount: company.events_count || 0
      }))
    }))
    .catch((error) => {
      console.error('Get company summary error:', error);
      throw error;
    });
};

/**
 * Get list of all company names in the system
 * @returns {Promise} Promise with company names
 */
const getCompanyList = async () => {
  return axios
    .get('company/list')
    .then((response) => {
      // Handle both array response and object response
      const companies = Array.isArray(response) ? response : (response.companies || [])
      return {
        success: true,
        companies
      }
    })
    .catch((error) => {
      console.error('Get company list error:', error);
      throw error;
    });
};

/**
 * Get company profile steps with prompts and outputs (Steps 1-6.5)
 * @param {string} companyName - Company name
 * @param {boolean} includePopulatedPrompt - Whether to include fully populated prompts (default: false)
 * @returns {Promise<Object>} Company profile steps data
 */
const getCompanyProfileStepsWithPrompts = async (companyName, includePopulatedPrompt = false) => {
  const params = new URLSearchParams();
  if (includePopulatedPrompt) params.append('include_populated_prompt', 'true');
  const queryString = params.toString();
  const url = `company/${encodeURIComponent(companyName)}/company-profile-steps-with-prompts${queryString ? '?' + queryString : ''}`;

  return axios
    .get(url)
    .then((response) => ({
      success: response.success !== false,
      companyName: response.company_name,
      includePopulatedPrompt: response.include_populated_prompt,
      steps: response.steps || {}
    }))
    .catch((error) => {
      console.error('Error fetching company profile steps:', error);
      throw error;
    });
};

/**
 * Get EOI generation steps with prompts and outputs
 * @param {string} companyName - Company name
 * @param {boolean} includePopulatedPrompt - Whether to include fully populated prompts (default: false)
 * @returns {Promise<Object>} EOI steps data
 */
const getEOIStepsWithPrompts = async (companyName, includePopulatedPrompt = false) => {
  const params = new URLSearchParams();
  if (includePopulatedPrompt) params.append('include_populated_prompt', 'true');
  const queryString = params.toString();
  const url = `company/${encodeURIComponent(companyName)}/eoi-steps-with-prompts${queryString ? '?' + queryString : ''}`;

  return axios
    .get(url)
    .then((response) => ({
      success: response.success !== false,
      companyName: response.company_name,
      includePopulatedPrompt: response.include_populated_prompt,
      steps: response.steps || {}
    }))
    .catch((error) => {
      console.error('Error fetching EOI steps:', error);
      throw error;
    });
};

/**
 * Get Supplier analysis steps with prompts and outputs
 * @param {string} companyName - Company name
 * @param {boolean} includePopulatedPrompt - Whether to include fully populated prompts (default: false)
 * @returns {Promise<Object>} Supplier steps data
 */
const getSupplierStepsWithPrompts = async (companyName, includePopulatedPrompt = false) => {
  const params = new URLSearchParams();
  if (includePopulatedPrompt) params.append('include_populated_prompt', 'true');
  const queryString = params.toString();
  const url = `company/${encodeURIComponent(companyName)}/supplier-steps-with-prompts${queryString ? '?' + queryString : ''}`;

  return axios
    .get(url)
    .then((response) => ({
      success: response.success !== false,
      companyName: response.company_name,
      includePopulatedPrompt: response.include_populated_prompt,
      steps: response.steps || {}
    }))
    .catch((error) => {
      console.error('Error fetching supplier steps:', error);
      throw error;
    });
};

/**
 * Per-dimension impact history (dimension ledger) for a company:
 * one group per dimension with the dated series of calls, plus the
 * sized drivers from the forecast decomposition.
 * @param {string} companyName - Company name
 * @returns {Promise<Object>} Dimension groups and driver groups
 */
const getCompanyDimensions = async (companyName) => {
  return axios
    .get(`company/${encodeURIComponent(companyName)}/dimensions`)
    .then((response) => ({
      success: response.success !== false,
      companyName: response.company_name,
      dimensions: response.dimensions || [],
      drivers: response.drivers || []
    }))
    .catch((error) => {
      console.error('Error fetching dimension ledger:', error);
      throw error;
    });
};


/**
 * Get available exposure/risk types for a company
 * @param {string} companyName - Company name
 * @returns {Promise<Object>} List of exposure types
 */
const getExposureTypes = async (companyName) => {
  return axios
    .get(`company/${encodeURIComponent(companyName)}/exposure-types`)
    .then((response) => ({
      success: response.success !== false,
      companyName: response.company_name,
      exposureTypes: response.exposure_types || [],
      count: response.count || 0
    }))
    .catch((error) => {
      console.error('Error fetching exposure types:', error);
      throw error;
    });
};

// ============================================================================
// Company Management Endpoints (CRUD operations on canonical companies table)
// ============================================================================

/**
 * Create a new company
 * @param {Object} params - Company creation parameters
 * @param {string} params.displayName - Company display name
 * @param {string} params.identifier - Company ticker or CIK
 * @param {string} params.region - Company region ('us' or 'non-us')
 * @returns {Promise<Object>} Created company data
 */
// ⚠ `createCompany` REMOVED. POST /company/manage is gone (backend spec 047 and the
// surface consolidation after it): a company is created in exactly ONE place, the
// add-company route on /onboarding. This page's "Add Company" buttons link there.

/**
 * Get all companies from the canonical companies table
 * @returns {Promise<Object>} List of all companies
 */
const getAllManagedCompanies = async () => {
  return axios
    .get('company/manage/all', { skipPortfolioHeader: true })
    .then((response) => ({
      success: response.success !== false,
      companies: (response.companies || []).map(company => ({
        id: company.id,
        displayName: company.display_name,
        identifier: company.identifier || company.company_identifier,
        normalizedName: company.normalized_name,
        region: company.region,
        // The brand mark and its key. `/company/manage/all` now returns both
        // (it used to omit them), so the admin list no longer needs a second
        // metadata fetch to show a logo, nor a lookup on the display name.
        logoUrl: company.logo_url || null,
        domain: company.domain || null,
        createdAt: company.created_at,
        updatedAt: company.updated_at
      }))
    }))
    .catch((error) => {
      console.error('Error fetching managed companies:', error);
      throw error;
    });
};

/**
 * Get a company by ID
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} Company data
 */
const getManagedCompanyById = async (companyId) => {
  return axios
    .get(`company/manage/${companyId}`)
    .then((response) => {
      // Backend returns CompanyResponse at the top level (no .company wrapper)
      const c = response?.company || response
      if (!c?.id) return { success: false, company: null }
      return {
        success: true,
        company: {
          id: c.id,
          displayName: c.display_name || c.displayName,
          identifier: c.identifier || c.company_identifier,
          normalizedName: c.normalized_name || c.normalizedName,
          region: c.region,
          // Entry point for IR document discovery — surfaced in the Document
          // Discovery tab so a wrong/missing domain is visible where it bites.
          domain: c.domain,
          createdAt: c.created_at || c.createdAt,
          updatedAt: c.updated_at || c.updatedAt,
        },
      }
    })
    .catch((error) => {
      console.error('Error fetching company by ID:', error);
      throw error;
    });
};

/**
 * Update a company's display name and/or region
 * @param {number} companyId - Company ID
 * @param {Object} params - Update parameters
 * @param {string} [params.displayName] - New display name
 * @param {string} [params.region] - New region ('us' or 'non-us')
 * @returns {Promise<Object>} Updated company data
 */
const updateCompany = async (companyId, { displayName, identifier, region, logoUrl }) => {
  const payload = {};
  if (displayName !== undefined) payload.display_name = displayName;
  if (identifier !== undefined) payload.identifier = identifier;
  if (region !== undefined) payload.region = region;
  if (logoUrl !== undefined) payload.logo_url = logoUrl;

  return axios
    .put(`company/manage/${companyId}`, payload)
    .then((response) => ({
      success: response.success !== false,
      company: response.company ? {
        id: response.company.id,
        displayName: response.company.display_name,
        identifier: response.company.identifier || response.company.company_identifier,
        normalizedName: response.company.normalized_name,
        region: response.company.region,
        createdAt: response.company.created_at,
        updatedAt: response.company.updated_at
      } : null,
      message: response.message
    }))
    .catch((error) => {
      console.error('Error updating company:', error);
      throw error;
    });
};

/**
 * Delete a company (Admin only)
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} Deletion result
 */
const deleteCompany = async (companyId) => {
  return axios
    .delete(`company/manage/${companyId}`)
    .then((response) => ({
      success: response.success !== false,
      message: response.message
    }))
    .catch((error) => {
      console.error('Error deleting company:', error);
      throw error;
    });
};

/**
 * Rename a company across all tables (Manager+ only)
 * Updates company name in companies, risks, documents, alerts, eois, etc.
 * Only updates exact matches of the current display name.
 * @param {number} companyId - Company ID
 * @param {string} newName - New company display name
 * @returns {Promise<Object>} Rename result with stats
 */
const renameCompany = async (companyId, newName) => {
  return axios
    .post(`company/manage/${companyId}/rename`, { new_name: newName })
    .then((response) => ({
      success: response.success !== false,
      message: response.message,
      companyId: response.company_id,
      oldName: response.old_name,
      newName: response.new_name,
      tablesUpdated: response.tables_updated || [],
      rowsUpdated: response.rows_updated || {}
    }))
    .catch((error) => {
      console.error('Error renaming company:', error);
      throw error;
    });
};

/**
 * Scan for company name variations across all tables (Manager+ only)
 * Uses normalized name matching to find different spellings/capitalizations.
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} Scan results with variations found
 */
const scanCompanyNames = async (companyId) => {
  return axios
    .get(`company/manage/${companyId}/scan-names`)
    .then((response) => ({
      success: response.success !== false,
      companyId: response.company_id,
      displayName: response.display_name,
      normalizedName: response.normalized_name,
      variations: response.variations || [],
      totalVariations: response.total_variations || 0,
      totalRows: response.total_rows || 0,
      message: response.message
    }))
    .catch((error) => {
      console.error('Error scanning company names:', error);
      throw error;
    });
};

/**
 * Rename selected company name variations to a single name (Manager+ only)
 * Replaces only the specified variations with the new name.
 * @param {number} companyId - Company ID
 * @param {string} newName - New company display name
 * @param {string[]} oldNames - List of old name variations to replace
 * @returns {Promise<Object>} Rename result with stats
 */
const renameSelectedVariations = async (companyId, newName, oldNames) => {
  return axios
    .post(`company/manage/${companyId}/rename-selected`, { new_name: newName, old_names: oldNames })
    .then((response) => ({
      success: response.success !== false,
      message: response.message,
      companyId: response.company_id,
      newName: response.new_name,
      oldNamesReplaced: response.old_names_replaced || [],
      oldNamesNotFound: response.old_names_not_found || [],
      tablesUpdated: response.tables_updated || [],
      rowsUpdated: response.rows_updated || {}
    }))
    .catch((error) => {
      console.error('Error renaming selected company variations:', error);
      throw error;
    });
};

/**
 * Get triggers for a company
 * @param {string} companyName - Company name
 * @returns {Promise<Object>} Triggers data grouped by exposure type
 */
const getTriggers = async (companyName) => {
  return axios
    .get(`company/${encodeURIComponent(companyName)}/triggers`)
    .then((response) => ({
      success: response.success !== false,
      companyName: response.company_name,
      exposures: (response.exposures || []).map(exp => ({
        exposureType: exp.exposure_type,
        triggers: (exp.triggers || []).map(t => ({
          title: t.title,
          why: t.why,
          sizingMethod: t.sizing_method,
          dataAnchors: t.data_anchors
        })),
        count: exp.count || 0,
        lastUpdated: exp.last_updated
      })),
      count: response.count || 0
    }))
    .catch((error) => {
      console.error('Error fetching triggers:', error);
      throw error;
    });
};

// ============================================================================
// Transfer (Export / Import) — async job-based endpoints
// ============================================================================

/**
 * Start an async company export job
 * @param {number} companyId - Numeric company ID
 * @param {'full'|'standard'|'minimal'} level - Export level
 * @returns {Promise<{jobId: string, status: string, message: string}>}
 */
const startExport = async (companyId, level = 'standard') => {
  return axios
    .post('company/transfer/export', { company_id: companyId, level })
    .then((response) => ({
      jobId: response.job_id,
      status: response.status,
      message: response.message
    }))
    .catch((error) => {
      console.error('Error starting export:', error);
      throw error;
    });
};

/**
 * Poll export job status
 * @param {string} jobId
 * @returns {Promise<{jobId: string, status: string, progress: string, fileReady: boolean, companyName: string|null, error: string|null}>}
 */
const getExportJobStatus = async (jobId) => {
  return axios
    .get(`company/transfer/export/job/${jobId}`)
    .then((response) => ({
      jobId: response.job_id,
      status: response.status,
      progress: response.progress,
      fileReady: response.file_ready,
      companyName: response.company_name || null,
      error: response.error || null
    }))
    .catch((error) => {
      console.error('Error polling export job:', error);
      throw error;
    });
};

/**
 * Download a completed export as a ZIP blob
 * @param {string} jobId
 * @returns {Promise<Blob>}
 */
const downloadExport = async (jobId) => {
  const instance = axios.getAxiosInstance();
  const config = axios.getConfig();
  return instance
    .get(`company/transfer/export/download/${jobId}`, {
      ...config,
      responseType: 'blob'
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error('Error downloading export:', error);
      throw error;
    });
};

/**
 * List past exports this user can download.
 * @param {number} limit
 * @returns {Promise<Array>} newest first
 */
const listExports = async (limit = 25) => {
  return axios
    .get('company/transfer/exports', { params: { limit } })
    .then((response) =>
      (response || []).map((r) => ({
        jobId: r.job_id,
        label: r.label,
        kind: r.kind,
        level: r.level,
        status: r.status,
        createdAt: r.created_at,
        completedAt: r.completed_at,
        exportedBy: r.exported_by,
        filename: r.filename,
        sizeBytes: r.size_bytes,
        companiesExported: r.companies_exported,
        companiesTotal: r.companies_total,
        companiesFailed: r.companies_failed || [],
        fileAvailable: r.file_available,
        error: r.error || null
      }))
    )
    .catch((error) => {
      console.error('Error listing exports:', error);
      throw error;
    });
};

/**
 * Download a finished export.
 *
 * Prefers a signed GCS link: the archive then goes straight from storage to the
 * browser. Streaming it back through the API cannot exceed Cloud Run's 32 MiB
 * response cap, which is what broke the 503 MB portfolio download on 10 Sep.
 * Only when the server says it has no bucket (local dev) do we fall back to the
 * streaming route and a blob.
 *
 * @param {string} jobId
 * @returns {Promise<void>} resolves once the download has been handed to the browser
 */
const downloadExportFile = async (jobId, fallbackName = 'export.zip') => {
  const link = await axios
    .get(`company/transfer/export/download-url/${jobId}`)
    .catch((error) => {
      console.error('Error getting export download URL:', error);
      throw error;
    });

  if (link && link.url) {
    const a = document.createElement('a');
    a.href = link.url;
    a.download = link.filename || fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }

  // No bucket to sign against — stream it through the API instead.
  const blob = await downloadExport(jobId);
  downloadBlob(blob, link?.filename || fallbackName);
};

/**
 * Preview a ZIP import file (validate before importing)
 * @param {File} file - ZIP file
 * @returns {Promise<{valid: boolean, companyName: string, tableCountss: Object, conflicts: string[], warnings: string[]}>}
 */
const previewImport = async (fileOrGcsObject) => {
  const formData = new FormData();
  // String → already in GCS (large-bundle path). File → direct upload (small
  // bundles / local dev without a bucket). Backend accepts either.
  if (typeof fileOrGcsObject === 'string') formData.append('gcs_object', fileOrGcsObject);
  else formData.append('file', fileOrGcsObject);
  return axios
    .post('company/transfer/import/preview', formData)
    .then((response) => ({
      valid: response.valid,
      companyName: response.company_name,
      companyIdentifier: response.company_identifier,
      exportLevel: response.export_level,
      tableCounts: response.table_counts || {},
      conflicts: response.conflicts || [],
      warnings: response.warnings || []
    }))
    .catch((error) => {
      console.error('Error previewing import:', error);
      throw error;
    });
};

/**
 * Get a signed URL to upload an import ZIP directly to GCS.
 * Lets large bundles bypass the API's 32 MiB request-body limit.
 *
 * When the server has no bucket configured (local dev) it answers
 * `directUpload: true` with no URL — the caller must post the file itself as
 * multipart instead. That flag is the only sanctioned route to the multipart
 * path: an actual signing failure in cloud still throws, so a GCS outage never
 * silently downgrades a large bundle onto the 32 MiB-capped request body.
 * @returns {Promise<{uploadUrl: string|null, gcsObject: string|null, contentType: string, directUpload: boolean}>}
 */
const getImportUploadUrl = async (filename, contentType = 'application/zip') => {
  return axios
    .post('company/transfer/upload-url', { filename, content_type: contentType })
    .then((response) => ({
      uploadUrl: response.upload_url || null,
      gcsObject: response.gcs_object || null,
      contentType: response.content_type,
      directUpload: Boolean(response.direct_upload)
    }))
    .catch((error) => {
      console.error('Error getting upload URL:', error);
      throw error;
    });
};

/**
 * PUT a file straight to GCS via a signed URL (bypasses the API entirely —
 * no baseURL, no auth header). Uses XHR for upload progress.
 * The Content-Type MUST match what the signed URL was created with.
 * @param {(pct:number)=>void|null} onProgress
 */
const uploadFileToGcs = (uploadUrl, file, contentType, onProgress = null) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload to storage failed (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Upload to storage failed (network error)'));
    xhr.send(file);
  });
};

/**
 * Stage an import ZIP and return the reference the preview/import endpoints
 * should use. Used by the single-company flow so preview AND import reference
 * the same upload.
 *
 * Normally that means uploading straight to GCS and returning the object key,
 * which bypasses the API's 32 MiB request-body limit for large bundles. When
 * the server reports local mode there is no bucket, so the file itself is
 * returned and the caller posts it as multipart — `previewImport` and
 * `startImport` both accept either form.
 * @param {File} file
 * @param {(pct:number)=>void|null} onProgress
 * @returns {Promise<string|File>} gcsObject key, or the file for direct upload
 */
const uploadImportFile = async (file, onProgress = null) => {
  const { uploadUrl, gcsObject, contentType, directUpload } = await getImportUploadUrl(file.name);
  if (directUpload) return file;
  await uploadFileToGcs(uploadUrl, file, contentType, onProgress);
  return gcsObject;
};

/**
 * Start an async company import job from a staged ZIP.
 * @param {string|File} fileOrGcsObject - object key from uploadImportFile, or
 *   the file itself when the server is in local mode (no bucket)
 * @param {'fail'|'replace'|'rename'} conflictStrategy
 * @param {number|null} targetPortfolioId
 * @returns {Promise<{jobId: string, status: string, message: string}>}
 */
const startImport = async (fileOrGcsObject, conflictStrategy = 'fail', targetPortfolioId = null) => {
  const formData = new FormData();
  if (typeof fileOrGcsObject === 'string') formData.append('gcs_object', fileOrGcsObject);
  else formData.append('file', fileOrGcsObject);
  formData.append('conflict_strategy', conflictStrategy);
  if (targetPortfolioId) formData.append('target_portfolio_id', String(targetPortfolioId));
  return axios
    .post('company/transfer/import', formData)
    .then((response) => ({
      jobId: response.job_id,
      status: response.status,
      message: response.message
    }))
    .catch((error) => {
      console.error('Error starting import:', error);
      throw error;
    });
};

/**
 * Poll import job status
 * @param {string} jobId
 * @returns {Promise<{jobId: string, status: string, progress: string, error: string|null, result: Object|null}>}
 */
const getImportJobStatus = async (jobId) => {
  return axios
    .get(`company/transfer/import/job/${jobId}`)
    .then((response) => ({
      jobId: response.job_id,
      status: response.status,
      progress: response.progress,
      error: response.error || null,
      result: response.result || null
    }))
    .catch((error) => {
      console.error('Error polling import job:', error);
      throw error;
    });
};

/**
 * Start an async portfolio export job (bundles all companies in a portfolio into one ZIP)
 * @param {number|null} portfolioId - Portfolio ID, or null for all companies (admin only)
 * @param {'full'|'standard'|'minimal'} level - Export level
 * @returns {Promise<{jobId: string, status: string, message: string}>}
 */
const startPortfolioExport = async (portfolioId, level = 'standard') => {
  return axios
    .post('company/transfer/export-portfolio', { portfolio_id: portfolioId, level })
    .then((response) => ({
      jobId: response.job_id,
      status: response.status,
      message: response.message
    }))
    .catch((error) => {
      console.error('Error starting portfolio export:', error);
      throw error;
    });
};

/**
 * Start an async portfolio import job (imports a ZIP of company ZIPs)
 * @param {File} file - Portfolio ZIP file
 * @param {'fail'|'replace'|'rename'} conflictStrategy
 * @param {number|null} targetPortfolioId
 * @returns {Promise<{jobId: string, status: string, message: string}>}
 */
const startPortfolioImport = async (
  file,
  conflictStrategy = 'fail',
  targetPortfolioId = null,
  onUploadProgress = null
) => {
  // 1) Upload the ZIP straight to GCS (bypasses the 32 MiB request limit), or
  //    carry the file through when the server reports local mode (no bucket).
  const { uploadUrl, gcsObject, contentType, directUpload } = await getImportUploadUrl(file.name);
  if (!directUpload) await uploadFileToGcs(uploadUrl, file, contentType, onUploadProgress);

  // 2) Kick off the import from the uploaded object, or from the file itself
  const formData = new FormData();
  if (directUpload) formData.append('file', file);
  else formData.append('gcs_object', gcsObject);
  formData.append('conflict_strategy', conflictStrategy);
  if (targetPortfolioId) formData.append('target_portfolio_id', String(targetPortfolioId));
  // Direct upload streams through this request, so progress belongs here.
  const config = directUpload && onUploadProgress
    ? {
        onUploadProgress: (e) => {
          if (e.total) onUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      }
    : {};
  return axios
    .post('company/transfer/import-portfolio', formData, config)
    .then((response) => ({
      jobId: response.job_id,
      status: response.status,
      message: response.message
    }))
    .catch((error) => {
      console.error('Error starting portfolio import:', error);
      throw error;
    });
};

export default {
  getTriggers,
  getCompanyMetadata,
  getCompanySummary,
  getCompanyList,
  getCompanyProfileStepsWithPrompts,
  getEOIStepsWithPrompts,
  getSupplierStepsWithPrompts,
  getExposureTypes,
  getCompanyDimensions,
  // Company management
  getAllManagedCompanies,
  getManagedCompanyById,
  updateCompany,
  deleteCompany,
  renameCompany,
  scanCompanyNames,
  renameSelectedVariations,
  // Transfer (Export/Import)
  startExport,
  getExportJobStatus,
  downloadExport,
  downloadExportFile,
  listExports,
  uploadImportFile,
  previewImport,
  startImport,
  getImportJobStatus,
  // Portfolio Transfer
  startPortfolioExport,
  startPortfolioImport
};
